import {useEffect} from 'react'
import {useStore} from 'react-redux'
import {useLocation} from 'react-router-dom'
import {reauthorizeIfTokenExpiring} from '@apis/helpers'
import type {RootState} from '@containers/App/store'
import {selectHasGuardedUnsavedChanges} from '@redux/selectors/customDashboardSelectors'

/**
 * Re-authorizes a stale tab before the user works in it (AB#6617). Checks on
 * mount (app start) and on every in-app navigation to another page, so the
 * round trip through the auth site lands on the page the user just opened and
 * reads as that page loading. Never on focus or visibility: coming back to the
 * tab and seeing it reload looked like the app refreshing by itself
 * (AB#6757). A query-only change (filters, tabs written to the URL) is not a
 * navigation. reauthorizeIfTokenExpiring decides whether the token is close
 * enough to its expiry and whether this tab may redirect at all (not in
 * Teams, not on a sign-in or public route, not with unsaved changes, at most
 * once a minute). Used once, by the signed-in shell (DashTemplate).
 */
export const useProactiveReauthorize = (): void => {
  const store = useStore<RootState>()
  const {pathname} = useLocation()

  useEffect(() => {
    void reauthorizeIfTokenExpiring({
      hasUnsavedChanges: () => selectHasGuardedUnsavedChanges(store.getState()),
    })
  }, [store, pathname])
}
